import { useEffect, useState } from 'react';

/**
 * Global-Level PWA-Install State
 * - Fängt `beforeinstallprompt` einmalig auf Window-Ebene ab
 * - Cached das Event, damit mehrere Komponenten (Prompt-Banner + Button)
 *   auf denselben Trigger reagieren können
 */

let cachedPrompt = null;
const listeners = new Set();

function notify() {
  for (const cb of listeners) {
    try { cb(); } catch { /* noop */ }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    cachedPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    cachedPrompt = null;
    notify();
    // GA4 + Google-Ads Conversion feuern
    try {
      import('../lib/gtag').then(({ trackGA4Event, trackConversion, CONVERSIONS }) => {
        const platform = /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'ios'
          : /Android/.test(navigator.userAgent) ? 'android'
          : 'desktop';
        trackGA4Event('pwa_installed', { source: 'native_event', platform });
        // Google Ads: feuert nur, wenn REACT_APP_GADS_PWA_INSTALL_LABEL in .env gesetzt ist
        trackConversion(CONVERSIONS.PWA_INSTALL, { value: 1.0, once: 'pwa_install' });
      });
    } catch { /* noop */ }
  });
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  // Alle iOS-Browser (Safari, Chrome/CriOS, Firefox/FxiOS) + iPadOS-Desktop-UA
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

export function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  return isIOS() || /Android/i.test(window.navigator.userAgent || '');
}

/**
 * Wartet bis zu `ms` auf das native beforeinstallprompt-Event.
 * Gibt das Event zurück oder null bei Timeout.
 */
export function waitForPrompt(ms = 2000) {
  if (cachedPrompt) return Promise.resolve(cachedPrompt);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      listeners.delete(cb);
      resolve(null);
    }, ms);
    const cb = () => {
      if (cachedPrompt) {
        clearTimeout(timer);
        listeners.delete(cb);
        resolve(cachedPrompt);
      }
    };
    listeners.add(cb);
  });
}

/**
 * Hook: usePWAInstall
 * @returns {{
 *   canInstall: boolean,          // Android/Chrome — natives Install-Event verfügbar
 *   installed: boolean,           // App läuft bereits im Standalone-Modus
 *   isIOSSafari: boolean,         // iOS Safari — natives Event unmöglich, Anleitung nötig
 *   promptInstall: () => Promise<boolean>  // löst den Install-Dialog aus
 * }}
 */
export default function usePWAInstall() {
  const [tick, setTick] = useState(0);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const cb = () => setTick((n) => n + 1);
    listeners.add(cb);
    // Falls Standalone-Mode nachträglich aktiviert wird
    const mq = window.matchMedia('(display-mode: standalone)');
    const mqHandler = () => setInstalled(isStandalone());
    mq.addEventListener?.('change', mqHandler);
    window.addEventListener('appinstalled', () => setInstalled(true));
    return () => {
      listeners.delete(cb);
      mq.removeEventListener?.('change', mqHandler);
    };
  }, []);

  const canInstall = Boolean(cachedPrompt) && !installed;
  const isIOSSafari = isIOS() && !installed;

  const promptInstall = async () => {
    if (!cachedPrompt) return false;
    cachedPrompt.prompt();
    const choice = await cachedPrompt.userChoice;
    if (choice?.outcome === 'accepted') {
      setInstalled(true);
      cachedPrompt = null;
      notify();
      return true;
    }
    return false;
  };

  return { canInstall, installed, isIOSSafari, promptInstall, tick };
}
